import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type ToastType = "success" | "error" | "info";

type Toast = {
  id: string;
  type: ToastType;
  title: string;
  detail?: string;
};

type ToastContextValue = {
  push: (t: Omit<Toast, "id">) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider(props: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const toast: Toast = { id, ...t };
    setToasts((prev) => [toast, ...prev].slice(0, 3));

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 3500);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {props.children}
      <div
        aria-live="polite"
        aria-atomic="false"
        role="status"
        className="pointer-events-none fixed right-4 top-4 z-[200] flex w-[360px] max-w-[90vw] flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={[
              "pointer-events-auto rounded-xl p-3 shadow-lg ring-1 toast-item",
              t.type === "success" ? "toast-success" : "",
              t.type === "error" ? "toast-error" : "",
              t.type === "info" ? "toast-info" : ""
            ].join(" ")}
          >
            <div className="text-sm font-semibold">{t.title}</div>
            {t.detail ? <div className="mt-0.5 text-sm opacity-90">{t.detail}</div> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return {
    success: (title: string, detail?: string) => ctx.push({ type: "success", title, detail }),
    error: (title: string, detail?: string) => ctx.push({ type: "error", title, detail }),
    info: (title: string, detail?: string) => ctx.push({ type: "info", title, detail })
  };
}


